/**
 * رموز الحركة (frontend-design §5.6) — القيم نهائية ولا تُشتق قيمة ثانية منها.
 *
 * المبادئ الملزمة: transform/opacity فقط، delight واحد لكل تفاعل، لا حركة دورية،
 * والتنقل بين الصفحات بلا انتقال (السرعة هي الأدب مع المشتت).
 */

/*
 * ولا `spring` في النظام: الحركة الزنبركية غير مقيَّدة بمدّة وتنتهي بارتداد،
 * وDESIGN.md §Motion صريحة: «150–250ms, ease-out only … **no bounce**». ما كان
 * يستعمله (`toastEnter`) صار على `dur2 + easeOut` كبقية الأسطح العائمة.
 */
export const motionTokens = {
  dur1: 0.12,
  dur2: 0.18,
  dur3: 0.24,
  dur4: 0.32,
  easeStandard: [0.2, 0, 0, 1] as [number, number, number, number],
  easeOut: [0.16, 1, 0.3, 1] as [number, number, number, number],
} as const;

/**
 * الحركة الاتجاهية تُعرَّف بمصطلحي start/end وتُترجم هنا وحده (§3.1-4):
 * الواجهة RTL ثابتة، فبداية السطر هي اليمين ⇒ الإزاحة من البداية موجبة.
 */
export const INLINE_START_SIGN = 1;

/**
 * حركة الصف: **الخروج وحده**.
 *
 * `initial: false` عن قصد وبالبند: DESIGN.md §Motion — «No entrance
 * choreography». تحميلُ قائمةٍ ليس تغيّر حالة، فرقصُ صفوفها عند كل فتح شاشة
 * تأخيرٌ يتكرر عشرات المرات يوميًا. أما **الخروج** فتغيّر حالة حقيقي (سبب
 * انحلّ، تذكير تمّ، انتظار وصل) ويستحق 180ms — ولذلك بقي `exit` كما هو.
 *
 * ووصول صفٍّ بالبثّ المباشر هو أيضًا تغيّر حالة، وله `liveEnter` أدناه.
 */
export const rowEnter = {
  initial: false,
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 8 * INLINE_START_SIGN },
  transition: { duration: motionTokens.dur2, ease: motionTokens.easeStandard },
} as const;

/** وصول حدث بالبثّ المباشر وحده (سجل النشاط): تلاشٍ + انزياح 8px من البداية. */
export const liveEnter = { opacity: 0, x: 8 * INLINE_START_SIGN } as const;

/** لوحة الالتقاط: scale 0.98→1 + تلاشٍ، dur4. */
export const paletteEnter = {
  initial: { opacity: 0, scale: 0.98 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.98 },
  transition: { duration: motionTokens.dur4, ease: motionTokens.easeOut },
} as const;

/** Sheet الجوال: صعود من الأسفل، dur3. */
export const sheetEnter = {
  initial: { y: '100%' },
  animate: { y: 0 },
  exit: { y: '100%' },
  transition: { duration: motionTokens.dur3, ease: motionTokens.easeOut },
} as const;

/** UndoToast: صعود قصير من الأسفل، dur2 بمنحنى الخروج — بلا ارتداد. */
export const toastEnter = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 12 },
  transition: { duration: motionTokens.dur2, ease: motionTokens.easeOut },
} as const;

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
